import {
	FocusFeature,
	FocusedFeatureTypes,
	TrainingRoomPhase,
} from 'common/types'

export type ActiveTool =
	| 'slides'
	| 'media'
	| 'whiteboard'
	| 'notepad'
	| 'poll'
	| 'knowledgeCheck'
	| 'quiz'
	| 'question'
	| 'screenShare'
	| 'groups'
	| 'groupSelection'
	| 'taskReview'
	| 'other'

export type BlockedMenuAction =
	| 'screenShare'
	| 'quiz'
	| 'question'
	| 'knowledgeCheck'
	| 'poll'
	| 'whiteboard'
	| 'notepad'
	| 'groups'
	| 'media'
	| 'microphone'

export const ACTIVE_TOOL_HINT_KEYS: Record<ActiveTool, string> = {
	slides: 'room_menu.hint.tool.slides',
	media: 'room_menu.hint.tool.media',
	whiteboard: 'room_menu.hint.tool.whiteboard',
	notepad: 'room_menu.hint.tool.notepad',
	poll: 'room_menu.hint.tool.poll',
	knowledgeCheck: 'room_menu.hint.tool.knowledge_check',
	quiz: 'room_menu.hint.tool.quiz',
	question: 'room_menu.hint.tool.question',
	screenShare: 'room_menu.hint.tool.screen_share',
	groups: 'room_menu.hint.tool.groups',
	groupSelection: 'room_menu.hint.tool.group_selection',
	taskReview: 'room_menu.hint.tool.task_review',
	other: 'room_menu.hint.tool.other',
}

export const BLOCKED_ACTION_HINT_KEYS: Record<BlockedMenuAction, string> = {
	screenShare: 'room_menu.hint.action.screen_share',
	quiz: 'room_menu.hint.action.quiz',
	question: 'room_menu.hint.action.question',
	knowledgeCheck: 'room_menu.hint.action.knowledge_check',
	poll: 'room_menu.hint.action.poll',
	whiteboard: 'room_menu.hint.action.whiteboard',
	notepad: 'room_menu.hint.action.notepad',
	groups: 'room_menu.hint.action.groups',
	media: 'room_menu.hint.action.media',
	microphone: 'room_menu.hint.action.microphone',
}

const focusedTool = (feature: FocusFeature): ActiveTool => {
	switch (feature.type) {
		case FocusedFeatureTypes.SLIDE:
			return 'slides'
		case FocusedFeatureTypes.AUDIO_STREAMING:
		case FocusedFeatureTypes.YOUTUBE_STREAMING:
			return 'media'
		case FocusedFeatureTypes.WHITEBOARD:
			return 'whiteboard'
		case FocusedFeatureTypes.NOTEPAD:
			return 'notepad'
		case FocusedFeatureTypes.POLLING_OPEN_ENDED_WORD_CLOUD:
		case FocusedFeatureTypes.POLLING_OPEN_ENDED_LIST:
		case FocusedFeatureTypes.POLLING_MULTIPLE_CHOICE:
			return 'poll'
		case FocusedFeatureTypes.KNOWLEDGE_CHECK:
			return 'knowledgeCheck'
		case FocusedFeatureTypes.ICEBREAKER:
			return 'quiz'
		case FocusedFeatureTypes.ScreenShare:
			return 'screenShare'
		case FocusedFeatureTypes.SIMPLE_GROUPING:
			return 'groups'
		case FocusedFeatureTypes.GROUPING:
			return 'groupSelection'
		default:
			// a focus type this app doesn't know yet: still explain the gray
			// menu rather than break the room
			return 'other'
	}
}

/**
 * The tool that grays out the trainer menu, i.e. the one the trainer has to
 * close first. Undefined when nothing blocks the menu.
 */
export const activeTool = (
	focusedFeature: FocusFeature | undefined,
	questionActive: boolean,
	phase: TrainingRoomPhase
): ActiveTool | undefined => {
	if (focusedFeature) return focusedTool(focusedFeature)
	if (questionActive) return 'question'
	if (phase === TrainingRoomPhase.SELECTING_GROUPS) return 'groupSelection'
	// the webinar room keeps its trainer menu while tasks are reviewed
	if (phase === TrainingRoomPhase.REVIEWING_TASKS) return 'taskReview'
	return undefined
}
